import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@hd/auth/useAuth";
import { useContactPanel } from "@hd/contact-panel/useContactPanel";
import CreditCardModal from "@hd/credit-card-modal/CreditCardModal";
import { useCreditCardModal } from "@hd/credit-card-modal/useCreditCardModal";
import { useHub } from "@hd/hub/useHub";
import useLogger from "@hd/logger/useLogger";
import { useRequestModal } from "@hd/new-request-modal/useRequestModal";
import { showErrorFeedback } from "@hd/toast/toasts";
import HdPageLoader from "@hd/ui-kit/hd-page-loader/HdPageLoader";
import { useWhatsappModal } from "@hd/whatsapp-modal/useWhatsappModal";

import { useSidebar } from "../layout/sidebar/sidebar.store";
import { useMiceQuotationRestoreModal } from "../mice-quotation-restore-modal/useMiceQuotationRestoreModal";
import { useLegacyHdBridgeStore } from "./legacy-hd-bridge.store";
import { CommunicationTypeEnum } from "@hd/types/dto/communication/communication-type.enum";
import { useCommunicationsModal } from "../modals/communications-modal/useCommunicationsModal";
import { TimelineEventTypeEnum } from "@hd/types/dto/contact/enums/timeline-event-type";
import { CommunicationEntityDomain } from "@hd/types/dto/communication/communication-entity-domain";
import { RealtimeRequestLockedDto } from "@hd/types/dto/contact/realtime-request-locked.dto";
import { RealtimeRequestUnlockedDto } from "@hd/types/dto/contact/realtime-request-unlocked.dto";
import { RealtimeUserInfoDto } from "@hd/types/dto/contact/realtime-user-info.dto";
import { UserType } from "@hd/types/dto/shared/enums/user-type";
import { RealtimeChatMessageDto } from "@hd/types/dto/realtime-message/realtime-chat-message.dto";
import { RealtimeMessageAction } from "@hd/types/dto/realtime-message/enums/realtime-message-action";

interface IFrameMessage {
  kind: string;
}

interface LoadedMessage extends IFrameMessage {
  kind: "loaded";
  url: string;
  height: number;
  title: string;
}

interface OpenContactPanelMessage extends IFrameMessage {
  kind: "showContactPanel";
  contactId: string;
  companyId: string;
  tabIndex: number;
}

interface OpenContactPanelActivityHistoryMessage extends IFrameMessage {
  kind: "showContactPanelActivityHistory";
  contactId: string;
  companyId: string;
  categoryId: TimelineEventTypeEnum;
  categoryItemId: string;
}

interface OpenWhatsappModalMessage extends IFrameMessage {
  kind: "openWhatsappModal";
  groupId: number;
  companyId: string;
  contactId: string;
  communicationType?: number;
  entityId: string | null;
  sendQuotation: boolean;
}

interface OpenCreditCardModalMessage extends IFrameMessage {
  kind: "openCreditCardModal";
  companyId: number;
  quotationId: string;
}

interface OpenMiceQuotationRestoreModalMessage extends IFrameMessage {
  kind: "openMiceQuotationRestoreModal";
  companyId: number;
  quotationId: string | null;
}

interface OpenConversationHubMessage extends IFrameMessage {
  kind: "openConversationHub";
  companyId: number;
}

interface OpenContactEditMessage extends IFrameMessage {
  kind: "openContactEdit";
  contactId: number;
}

interface OpenLeisureReportsMessage extends IFrameMessage {
  kind: "openLeisureReports";
  companyId: number;
}

interface OpenNewRequestModalMessage extends IFrameMessage {
  kind: "openNewRequestModal";
  companyId: number;
}

interface OpenRequestModalMessage extends IFrameMessage {
  kind: "openRequestModal";
  requestDate: string;
  contactId: number;
}

interface OpenCommunicationsModalMessage extends IFrameMessage {
  kind: "openCommunicationsModal";
  canEditEmail: boolean;

  groupId: number;
  companyId: number;
  contactId: number;

  entityDomain: CommunicationEntityDomain;
  entityId: string;

  actionCode: string;
  communicationType: CommunicationTypeEnum;
}

interface ConnectHubMessage extends IFrameMessage {
  kind: "connectHub";
  companyId: number;
  userId?: string;
}

function LegacyHdBridgePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { debug } = useLogger();
  const { getToken } = useAuth();

  // const setUrl = useLegacyBridge((s) => s.setUrl);
  const [loading, setLoading] = useState(true);

  const [iframeState, setIframeState] = useState(0);

  const currentUrl = useRef("");

  const timeoutTimer = useRef<number | null>(null);

  const { setCurrentUrl: setUrl } = useSidebar();

  const { open: openContactPanel } = useContactPanel();
  const { openActivityHistoryAtItem } = useContactPanel();
  const { open: openWhatsappModal } = useWhatsappModal();
  const { open: openCreditCardModal } = useCreditCardModal();
  const { open: openRequestModal } = useRequestModal();
  const { open: openMiceQuotationRestoreModal } =
    useMiceQuotationRestoreModal();
  const {open: openCommunicationsModal} = useCommunicationsModal();
  const [hubConnectionData, setHubConnectionData] = useState<{
    companyId: number | null;
    userId: string | undefined;
  } | null>(null);

  const prevRouterUrl = useRef("");
  const legacyIFrame = useRef<HTMLIFrameElement | null>(null);

  const registerLegacySender = useLegacyHdBridgeStore((s) => s.register);

  useEffect(() => {
    registerLegacySender((msg) => {
      legacyIFrame.current?.contentWindow?.postMessage(msg, import.meta.env.VITE_HD_LEGACY_URL);
    });
  }, [registerLegacySender]);

  // Funzione per inviare il JWT token alla parte legacy per permettere di fare chiamate a WebApi da legacy
  const sendTokenToLegacy = useCallback(() => {
    if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
      const token = getToken();
      if (token) {
        legacyIFrame.current.contentWindow.postMessage(
          {
            kind: "authToken",
            token: token,
            apiBaseUrl: import.meta.env.VITE_HD_API_URL,
          },
          import.meta.env.VITE_HD_LEGACY_URL
        );
      }
    }
  }, [getToken]);

  useEffect(() => {
    const routerUrl = location.pathname + location.hash + location.search;
    if (
      !!currentUrl.current &&
      routerUrl !== currentUrl.current &&
      prevRouterUrl.current === routerUrl
    ) {
      setLoading(true);
      setIframeState((prev) => prev + 1);
    } else {
      prevRouterUrl.current = routerUrl;
    }
  }, [location, navigate, openCreditCardModal]);

  const onMessageReceived = useCallback(
    (data: IFrameMessage) => {
      if (data.kind === "loading") {
        setLoading(true);
        // if (!loading) {
        //   console.log("loading changed, triggering rendering");
        // } else {
        //   console.log("loading not changed, should not trigger rendering");
        // }
      } else if (data.kind === "loaded") {
        const message = data as LoadedMessage;
        setLoading(false);

        setUrl(message.url as string);
        document.title = message.title;
        window.history.replaceState(
          null,
          message.title,
          window.location.origin + message.url,
        );
        // navigate(message.url, { replace: true });
        currentUrl.current = message.url;
      } else if (data.kind === "showContactPanel") {
        // console.log("showContactPanel");

        const message = data as OpenContactPanelMessage;

        openContactPanel(
          Number(message.contactId),
          message.companyId == null ? -1 : Number(message.companyId),
          message.tabIndex,
        );
      } else if (data.kind === "showContactPanelActivityHistory") {
        const message = data as OpenContactPanelActivityHistoryMessage;

        openActivityHistoryAtItem(
          Number(message.contactId),
          message.companyId == null ? -1 : Number(message.companyId),
          message.categoryId,
          message.categoryItemId
        );
      } else if (data.kind === "openWhatsappModal") {
        const message = data as OpenWhatsappModalMessage;
        openWhatsappModal(
          Number(message.groupId),
          Number(message.companyId),
          Number(message.contactId),
          message.communicationType ?? null,
          message.entityId,
          !!message.sendQuotation
        );
      } else if (data.kind === "openContactEdit") {
        const message = data as OpenContactEditMessage;
        navigate(`/contacts-edit/${message.contactId}`);
      } else if (data.kind === "openLeisureReports") {
        const message = data as OpenLeisureReportsMessage;
        navigate(`/leisure-reports?companyId=${message.companyId}`);
      } else if (data.kind === "openCreditCardModal") {
        const message = data as OpenCreditCardModalMessage;
        openCreditCardModal(message.quotationId, message.companyId);
      } else if (data.kind === "openMiceQuotationRestoreModal") {
        const message = data as OpenMiceQuotationRestoreModalMessage;
        openMiceQuotationRestoreModal(message.companyId, message.quotationId);
      } else if (data.kind === "openConversationHub") {
        const message = data as OpenConversationHubMessage;
        navigate(`/conversation-hub?companyId=${message.companyId}`);
      } else if (data.kind === "openCommunicationsPostStay"){
        navigate('/communications/post-stay');
      } else if (data.kind === "openNewRequestModal") {
        const message = data as OpenNewRequestModalMessage;
        openRequestModal(0, null, message.companyId);
      } else if (data.kind === "openRequestModal") {
        const message = data as OpenRequestModalMessage;
        openRequestModal(0, null, undefined, {
          contactId: message.contactId,
          requestDate: new Date(message.requestDate).toISOString(),
        });
      } else if (data.kind === "openCommunicationsModal"){
        const message = data as OpenCommunicationsModalMessage;
        openCommunicationsModal(message.canEditEmail, message.groupId, message.companyId, message.contactId, message.entityDomain, message.entityId, message.actionCode, message.communicationType);
      } else if (data.kind === "connectHub") {
        const message = data as ConnectHubMessage;

        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge connection postMessage - message: ", message);
        }

        setHubConnectionData({
          companyId: message.companyId,
          userId: message.userId,
        });
      } else if (data.kind === "disconnectHub") {
        setHubConnectionData(null);
      } else if (data.kind === "requestToken") {
        // Richiesta del token corrente dal frame legacy per poter fare chiamate a WebApi
        sendTokenToLegacy();
      }
    },
    [
      setLoading,
      setUrl,
      openContactPanel,
      openActivityHistoryAtItem,
      openWhatsappModal,
      openMiceQuotationRestoreModal,
      navigate,
      debug,
      openCreditCardModal,
      openRequestModal,
      openCommunicationsModal,
      sendTokenToLegacy
    ]
  );

  const onMessages = useCallback(
    (messages: RealtimeChatMessageDto[]) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge newMessage postMessage - message: ", messages);
        }

        //Al momento mandiamo al legacy solo i messaggi in stato sent, per mantenere il vecchio comportamento che è già gestito nel legacy
        messages.filter(m => m.ActionId === RealtimeMessageAction.New).sort((a, b) => a.ActionTimestamp - b.ActionTimestamp)
        .forEach( m => {
          legacyIFrame.current?.contentWindow?.postMessage(
            {
              kind: "newMessage",
              message: m,
            },
            import.meta.env.VITE_HD_LEGACY_URL,
        );
        });
      }
    },
    [debug],
  );

  const onUsers = useCallback(
    (users: RealtimeUserInfoDto[]) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug(
            "LegacyHDBridge updateUserStatus postMessage - person: ",
            users,
          );
        }

        users.filter( u => u.Type === UserType.Person)
          .sort( (a, b) => a.Timestamp - b.Timestamp)
          .forEach( userInfo => {
            legacyIFrame.current?.contentWindow?.postMessage(
              {
                kind: "updateUserStatus",
                person: userInfo,
              },
              import.meta.env.VITE_HD_LEGACY_URL,
            );
          });     
        
      }
    },
    [debug],
  );

  const onQuotationLocked = useCallback(
    (data: RealtimeRequestLockedDto) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge lockQuotation postMessage - data: ", data);
        }

        legacyIFrame.current.contentWindow.postMessage(
          {
            kind: "lockQuotation",
            quotationData: data,
          },
          import.meta.env.VITE_HD_LEGACY_URL,
        );
      }
    },
    [debug],
  );

  const onQuotationUnlocked = useCallback(
    (data: RealtimeRequestUnlockedDto) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge unlockQuotation postMessage - data: ", data);
        }

        legacyIFrame.current.contentWindow.postMessage(
          {
            kind: "unlockQuotation",
            quotationData: data,
          },
          import.meta.env.VITE_HD_LEGACY_URL,
        );
      }
    },
    [debug],
  );

  const onRequestLocked = useCallback(
    (data: RealtimeRequestLockedDto) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge lockRequest postMessage - data: ", data);
        }

        legacyIFrame.current.contentWindow.postMessage(
          {
            kind: "lockRequest",
            requestData: data,
          },
          import.meta.env.VITE_HD_LEGACY_URL,
        );
      }
    },
    [debug],
  );

  const onRequestUnlocked = useCallback(
    (data: RealtimeRequestUnlockedDto) => {
      if (legacyIFrame.current && legacyIFrame.current.contentWindow) {
        if (Number(import.meta.env.VITE_HD_LOG_LEVEL) > 1) {
          debug("LegacyHDBridge unlockRequest postMessage - data: ", data);
        }

        legacyIFrame.current.contentWindow.postMessage(
          {
            kind: "unlockRequest",
            requestData: data,
          },
          import.meta.env.VITE_HD_LEGACY_URL,
        );
      }
    },
    [debug],
  );

  useHub(
    hubConnectionData?.companyId || null,
    {
      onMessages,
      onUsers,
      onQuotationLocked,
      onQuotationUnlocked,
      onRequestLocked,
      onRequestUnlocked,
    },
    hubConnectionData?.userId,
  );

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data === "reloadframe") {
        setLoading(true);
        setIframeState((prev) => prev + 1);
      }
    };
    window.addEventListener("message", handler);
    return () => {
      window.removeEventListener("message", handler);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
  }, [setLoading]);

  useEffect(() => {
    if (loading) {
      timeoutTimer.current = window.setTimeout(() => {
        if (loading) {
          setLoading(false);

          showErrorFeedback("Si è verificato un errore durante il caricamento");
        }
      }, 50000);
    } else {
      if (timeoutTimer.current) {
        clearTimeout(timeoutTimer.current);
        timeoutTimer.current = null;
      }
    }
  }, [loading, setLoading]);

  useEffect(() => {
    function messageHandler(event: MessageEvent<IFrameMessage>) {
      if (event.origin === import.meta.env.VITE_HD_LEGACY_URL) {
        onMessageReceived(event.data);
      }
    }

    // run useEffect only once
    // console.log("running effect! Add event listener");
    addEventListener("message", messageHandler);

    return () => {
      // console.log("remove event listener");
      removeEventListener("message", messageHandler);
    };
  }, [onMessageReceived]);

  return (
    <>
      {loading && <HdPageLoader></HdPageLoader>}
      <iframe
        ref={legacyIFrame}
        width="100%"
        style={{ minHeight: "100%" }}
        referrerPolicy="origin"
        key={iframeState}
        src={
          import.meta.env.VITE_HD_LEGACY_URL +
          location.pathname +
          location.hash +
          location.search
        }
        allow="clipboard-read; clipboard-write"
      ></iframe>
      <CreditCardModal></CreditCardModal>
    </>
  );
}

export default LegacyHdBridgePage;
